/**
 * Converts a BlendMaterial (linear RGB tuples, Blender field names)
 * to a SceneMaterial (hex strings, Three.js field names).
 */

import type { BlendMaterial } from "../blend-reader/types/BlendMaterial.ts";
import type { SceneMaterial } from "../modeling/types.ts";

/** Convert a linear [0,1] RGB tuple to a hex color string. */
function rgbToHex(rgb: readonly [number, number, number]): string {
  const r = Math.round(Math.min(1, Math.max(0, rgb[0])) * 255);
  const g = Math.round(Math.min(1, Math.max(0, rgb[1])) * 255);
  const b = Math.round(Math.min(1, Math.max(0, rgb[2])) * 255);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** True if the RGB tuple is effectively black [0,0,0]. */
function isBlack(rgb: readonly [number, number, number]): boolean {
  return rgb[0] === 0 && rgb[1] === 0 && rgb[2] === 0;
}

/**
 * Convert a BlendMaterial to a SceneMaterial.
 *
 * Only sets optional fields when they differ from SceneMaterial defaults
 * to keep the output compact.
 */
export function blendMaterialToScene(mat: BlendMaterial): SceneMaterial {
  const result: SceneMaterial = {
    color: rgbToHex(mat.baseColor),
    roughness: mat.roughness,
    metalness: mat.metallic,
  };

  // Emissive — only set when non-zero
  if (mat.emissionStrength > 0 && !isBlack(mat.emissionColor)) {
    result.emissiveColor = rgbToHex(mat.emissionColor);
    result.emissiveIntensity = mat.emissionStrength;
  }

  // Opacity — only set when non-opaque
  if (mat.alpha < 1) {
    result.opacity = mat.alpha;
  }

  // Specular — only set when non-default (0.5)
  if (mat.specular !== 0.5) {
    result.specularIntensity = mat.specular;
  }

  // Clearcoat
  if (mat.clearcoat > 0) {
    result.clearcoat = mat.clearcoat;
    result.clearcoatRoughness = mat.clearcoatRoughness;
  }

  // Sheen
  if (mat.sheen > 0) {
    result.sheen = mat.sheen;
    result.sheenRoughness = mat.sheenRoughness;
    if (!isBlack(mat.sheenTint)) {
      result.sheenColor = rgbToHex(mat.sheenTint);
    }
  }

  // Transmission / glass
  if (mat.transmission > 0) {
    result.transmission = mat.transmission;
    result.ior = mat.ior;
  }

  // Normal map strength — only set when non-default
  if (mat.normalStrength !== 1) {
    result.normalIntensity = mat.normalStrength;
  }

  return result;
}
